import "./index.scss";

import { useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import RadioButton from "common/form/inputs/radio";
import SectionHeader from "common/form/sub_form/section/header";
import NotaryInstructionsGrid from "common/details/instructions/instruction";
import TipWell from "common/core/tip_well";
import { useProofRebrand } from "util/feature_detection";

import type { NotaryNotesTransaction as Transaction } from "./index_fragment.graphql";

const messages = defineMessages({
  notaryNotePlaceholder: {
    id: "a3c04eba-4339-46db-b505-d6a3b30aad0c",
    defaultMessage: "Note",
  },
  notaryNoteSubHeaderTitle: {
    id: "9d1f4da7-25e6-42c1-8947-296c144bd006",
    defaultMessage: "Do you need to include any special instructions for the notary?",
  },
  selfNoteSubHeaderTitle: {
    id: "853890fc-3ec1-42c2-9d1e-30382f70dace",
    defaultMessage: "Do you want to include any notes for yourself?",
  },
  notaryNoteSubHeaderTipTitle: {
    id: "803b9625-4153-48a8-b3ea-53e6ab7c9053",
    defaultMessage: "Note for Notarize",
  },
  rebrandNotaryNoteSubHeaderTipTitle: {
    id: "21792200-8b0e-4e28-af0e-49a5ff154da7",
    defaultMessage: "Note for Notary",
  },
  selfNoteSubHeaderTipTitle: {
    id: "b97b4d03-2875-4e86-94a3-bd3a92443d4f",
    defaultMessage: "Note for yourself",
  },
  notaryNoteSubHeaderSubtitle: {
    id: "92b66e68-f016-4942-8c16-045ccba7a6b0",
    defaultMessage:
      "You can leave a note here if the signing requires any special instructions and the notary will be able to review it before they begin the signing process.",
  },
  selfNoteSubHeaderSubtitle: {
    id: "e643be10-e31b-4b6f-83eb-c41cf560659a",
    defaultMessage:
      "You can leave a note here if the signing requires any special instructions and you will be able to see it before beginning the signing process.",
  },
  no: {
    id: "4ee6c1e1-84d7-4166-9fe8-1cbdcd29df21",
    defaultMessage: "No",
  },
  yes: {
    id: "fc57ea0a-e3da-42b9-9214-644894a53106",
    defaultMessage: "Yes",
  },
});

type Props = {
  onUpdateNotaryInstruction: (instruction: { id: string; text: string }) => void;
  onDeleteNotaryInstruction: (id: string) => void;
  transaction: Transaction;
  selfNotes?: boolean;
};

export default function NotaryNotesSection({
  onUpdateNotaryInstruction,
  onDeleteNotaryInstruction,
  transaction,
  selfNotes = false,
}: Props) {
  const intl = useIntl();
  const { instructions } = transaction.document_bundle!;
  const hasNotes = instructions.length > 0;
  const [showNotesForm, setShowNotesForm] = useState(hasNotes);
  const isProof = useProofRebrand();

  return (
    <div className="NotaryNotesSection">
      <SectionHeader
        title={
          selfNotes
            ? intl.formatMessage(messages.selfNoteSubHeaderTitle)
            : intl.formatMessage(messages.notaryNoteSubHeaderTitle)
        }
        subtitle={
          showNotesForm
            ? null
            : selfNotes
            ? intl.formatMessage(messages.selfNoteSubHeaderSubtitle)
            : intl.formatMessage(messages.notaryNoteSubHeaderSubtitle)
        }
      />
      {(!hasNotes || !showNotesForm) && (
        <div className="NotaryNotesSection--radioButtons">
          <RadioButton
            onChange={() => setShowNotesForm(false)}
            radioValue={false}
            groupValue={showNotesForm}
            labelText={intl.formatMessage(messages.no)}
            className="NotaryNotesSection--radioButtons--button"
            size="small"
            automationId="notary-note-option-no"
          />
          <RadioButton
            onChange={() => setShowNotesForm(true)}
            radioValue
            groupValue={showNotesForm}
            className="NotaryNotesSection--radioButtons--button"
            labelText={intl.formatMessage(messages.yes)}
            size="small"
            automationId="notary-note-option-yes"
          />
        </div>
      )}
      {showNotesForm && (
        <div className="SubFormSection">
          <NotaryInstructionsGrid
            instructions={instructions}
            onDelete={onDeleteNotaryInstruction}
            onSave={onUpdateNotaryInstruction}
            allowChanges
          />
          <TipWell
            heading={
              selfNotes
                ? intl.formatMessage(messages.selfNoteSubHeaderTipTitle)
                : isProof
                ? intl.formatMessage(messages.rebrandNotaryNoteSubHeaderTipTitle)
                : intl.formatMessage(messages.notaryNoteSubHeaderTipTitle)
            }
          >
            {selfNotes
              ? intl.formatMessage(messages.selfNoteSubHeaderSubtitle)
              : intl.formatMessage(messages.notaryNoteSubHeaderSubtitle)}
          </TipWell>
        </div>
      )}
    </div>
  );
}
